import { useEffect, useState } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';
import type { FaqItem, Lang } from '../../types/cms';
import type { AdminCommon } from '../../pages/Admin';

const LANGS: { code: Lang; label: string }[] = [
  { code: 'tc', label: '繁體' },
  { code: 'sc', label: '简体' },
  { code: 'en', label: 'EN' },
];

const COPY_FIELDS: { key: 'title' | 'subtitle' | 'intro' | 'still' | 'cta' | 'lastUpdatedLabel'; label: string }[] = [
  { key: 'title', label: '頁面標題' },
  { key: 'subtitle', label: '副標題' },
  { key: 'intro', label: '前言' },
  { key: 'still', label: '「仍有疑問？」標題' },
  { key: 'cta', label: 'CTA 按鈕文字' },
  { key: 'lastUpdatedLabel', label: '更新日期標籤（如：資料更新；留空則不顯示）' },
];

export default function FaqManager({ draft, onSave, notify }: AdminCommon) {
  const [items, setItems] = useState<FaqItem[]>(draft.faq?.items || []);
  const [copy, setCopy] = useState(draft.faq?.copy);
  const [enabled, setEnabled] = useState(draft.faq?.enabled !== false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems(draft.faq?.items || []);
    setCopy(draft.faq?.copy);
    setEnabled(draft.faq?.enabled !== false);
  }, [draft]);

  const patchItem = (idx: number, fields: Partial<FaqItem>) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...fields } : it)));

  const addItem = () => {
    const id = window.prompt('新問題的 ID（英文短代號，如 ipo-cost）：');
    if (!id) return;
    if (items.some((it) => it.id === id)) {
      notify('ID 已存在。', 'error');
      return;
    }
    setItems([
      ...items,
      {
        id,
        q: { en: '', tc: '', sc: '' },
        a: { en: '', tc: '', sc: '' },
      },
    ]);
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      await onSave({ faq: { enabled, copy, items } });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page copy */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-navy-900">FAQ 頁面文字</h2>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            啟用 FAQ 頁
          </label>
        </div>
        <div className="space-y-4">
          {COPY_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <p className="text-xs text-gray-400 mb-1.5">{label}</p>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {LANGS.map(({ code, label: langLabel }) => (
                  <div key={code}>
                    <label className="block text-[11px] text-gray-400 mb-1">{langLabel}</label>
                    {key === 'intro' ? (
                      <textarea
                        rows={3}
                        className="w-full border border-gray-200 rounded p-2 text-sm"
                        value={copy?.[code]?.[key] || ''}
                        onChange={(e) =>
                          setCopy((prev: any) => ({ ...prev, [code]: { ...prev?.[code], [key]: e.target.value } }))
                        }
                      />
                    ) : (
                      <input
                        className="w-full border border-gray-200 rounded p-2 text-sm"
                        value={copy?.[code]?.[key] || ''}
                        onChange={(e) =>
                          setCopy((prev: any) => ({ ...prev, [code]: { ...prev?.[code], [key]: e.target.value } }))
                        }
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Items */}
      <div className="bg-white rounded-lg shadow-sm border p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-navy-900">問答項目（{items.length} 條）</h2>
          <div className="flex gap-2">
            <button
              onClick={addItem}
              className="flex items-center gap-1 text-sm px-4 py-2 rounded border border-navy-200 text-navy-700 hover:bg-navy-50"
            >
              <Plus className="h-4 w-4" /> 新增問題
            </button>
            <button
              onClick={saveAll}
              disabled={saving}
              className="flex items-center gap-1 text-sm px-4 py-2 rounded bg-gold-500 text-navy-900 font-bold hover:bg-gold-600 disabled:opacity-50"
            >
              <Save className="h-4 w-4" /> {saving ? '儲存中…' : '儲存全部到草稿'}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {items.map((item, idx) => (
            <div key={item.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">{item.id}</p>
                <button
                  onClick={() => setItems(items.filter((it) => it.id !== item.id))}
                  className="p-1.5 text-red-400 hover:text-red-600"
                  title="刪除"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {(['q', 'a'] as const).map((side) => (
                <div key={side} className="mb-3 last:mb-0">
                  <p className="text-xs text-gray-400 mb-1.5">{side === 'q' ? '問題' : '答案'}</p>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    {LANGS.map(({ code, label: langLabel }) => (
                      <div key={code}>
                        <label className="block text-[11px] text-gray-400 mb-1">{langLabel}</label>
                        <textarea
                          rows={side === 'a' ? 4 : 2}
                          className="w-full border border-gray-200 rounded p-2 text-sm"
                          value={item[side][code] || ''}
                          onChange={(e) =>
                            patchItem(idx, { [side]: { ...item[side], [code]: e.target.value } } as Partial<FaqItem>)
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {items.length === 0 && <p className="text-center text-gray-400 py-8">沒有問答項目。</p>}
        </div>
      </div>

      <p className="text-xs text-gray-400 px-1">
        注意：FAQ 的可見文字與頁面 JSON-LD（FAQPage 結構化資料）由同一份資料生成，編輯後兩者自動保持一致。
      </p>
    </div>
  );
}
